<template>
	<v-card class="category-card elevation-0 d-flex flex-column pt-2">
		<div class="category-card__container">
			<div class="category-card__outer">
				<div class="d-flex category-card__list">
					<ProfileBadge v-if="category.sponsored_by" isPin :pin="pinnedType(category)" />

					<div
						class="d-flex justify-space-between px-3 py-3"
						v-bind:style="{
							marginTop: category.sponsored_by ? '30px' : 0,
						}"
					>
						<router-link
							:to="{
								name: 'forums-by-category-id',
								params: { id: category.category_id },
							}"
							class="d-flex category-card__title"
						>
							<div class="category-card__meta mr-2">{{ category.category_name }}</div>
						</router-link>
						<Icon v-if="category.pin_to_top" :size="16" type="pinned" />
					</div>

					<v-divider v-if="category.lastActiveForum" />

					<router-link
						v-if="lastActiveForum.forum_text"
						:to="{
							name: 'forums-by-category-id',
							params: { id: category.category_id },
						}"
						class="d-flex flex-column category-card__title text-decoration-none"
					>
						<router-link
							:to="{
								name: isCurrentUser(this.user.user_id) ? 'profile' : 'user-by-id',
								params: isCurrentUser(user.user_id) ? {} : { id: user.user_id },
							}"
							class="d-flex align-center justify-space-between text-decoration-none px-3 pt-3"
							:class="{ disabled: this.user.user_hidden }"
						>
							<div class="d-flex align-center" v-if="category.lastActiveForum">
								<Avatar
									class="mr-3 category-card__avatar"
									:size="50"
									:avatar="user.user_logo"
								/>
								<div class="category-card__user-info-container">
									<div class="category-card__author">
										{{ user.visible_username }}
									</div>
									<div class="category-card__second-row">
										{{ user.second_profile_row }}
									</div>
								</div>
							</div>
							<div class="category-card__date" v-if="lastActiveForum.created">
								{{ calcDateAgo(lastActiveForum.created) }}
							</div>
						</router-link>
						<div
							class="forum-card__forum-text pb-3 pt-3 px-3"
							v-html="formatForumText(lastActiveForum.forum_text || '')"
						></div>
					</router-link>

					<v-divider />

					<div class="d-flex justify-space-between align-center py-3 px-3">
						<div class="d-flex">
							<div class="d-flex">
								<div class="category-card__counter-num">
									{{ category.forum_count }}
								</div>
								<span class="category-card__view-text ml-1">posts</span>
							</div>
							<div class="d-flex ml-4">
								<div class="category-card__counter-num">
									{{ category.forum_reply_count }}
								</div>
								<span class="category-card__view-text ml-1">replies</span>
							</div>
						</div>
						<div
							class="category-card__follow"
							:class="isFollowed && 'category-card__follow-active'"
							style="cursor: pointer"
							@click="onFollow"
						>
							{{ isFollowed ? 'Joined' : 'Join' }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { threadCategoryToggleFollow, threadCategoryToggleUnFollow } from '@/api/forum'
import Avatar from '@/components/Avatar'
import { mapGetters } from 'vuex'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import ProfileBadge from '@/components/ProfileBadge.vue'
import moment from 'moment'
import Dwh from '@/mixins/Dwh'
import linkifyThreadText from '@/mixins/linkifyThreadText'

Vue.use(VueScrollTo)

export default {
	name: 'CategoryCard',
	mixins: [Dwh, linkifyThreadText],
	components: {
		Avatar,
		ProfileBadge,
	},

	props: {
		category: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		return {
			isFollowed: false,
			pinnedIcon,
			sponsoredStarIcon,
		}
	},

	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		lastActiveForum() {
			return this.category.lastActiveForum || {}
		},

		user() {
			if (this.category.lastActiveForum) {
				return this.category.lastActiveForum.user
			}

			return {}
		},
	},

	methods: {
		onFollow() {
			if (this.isFollowed) {
				this.sendTrigger({
					category: 'Content',
					action: 'click_unjoin_forum_category',
					module: 'Forums',
					data: this.category.category_id,
					title: this.category.category_name,
				})
				threadCategoryToggleUnFollow(this.category.category_id)
			} else {
				this.sendTrigger({
					category: 'Content',
					action: 'click_join_forum_category',
					module: 'Forums',
					data: this.category.category_id,
					title: this.category.category_name,
				})
				threadCategoryToggleFollow({ thread_category_id: this.category.category_id })
			}

			this.isFollowed = !this.isFollowed
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		pinnedType(forum) {
			let result = {}

			if (forum.pin_to_top && forum.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + forum.sponsored_by,
				})
			} else if (forum.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + forum.sponsored_by,
				})
			} else if (forum.pin_to_top) {
				result = { icon: pinnedIcon, text: 'Pinned' }
			} else {
				result = { icon: pinnedIcon, text: 'Pinned' }
			}

			return result
		},
		calcDateAgo(date) {
			return moment(date * 1000).fromNow()
		},
	},

	created() {
		if (this.category) {
			this.isFollowed = this.category.follow
		}
	},
}
</script>

<style lang="scss">
.category-card {
	background: #f1f1f1 !important;
	&__view-text {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		color: #9b9b9b;
	}
	&__title {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0;
		color: #002855;
		text-decoration: unset;
		a {
			color: var(--link) !important;
		}
	}
	&__user-info-container {
		display: flex;
		height: 46px;
		flex-direction: column;
		justify-content: space-between;
	}

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 8px !important;
		background: #ffffff !important;
	}

	&__counter {
		margin-right: 24px;
		@media (max-width: 480px) {
			margin: 0;
		}

		&-wrapper {
			display: flex;
			@media (max-width: 480px) {
				flex-direction: column;
				align-items: flex-end;
			}
		}

		&-num {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #9b9b9b;
			text-align: right;
			display: flex;
			align-items: center;
		}
	}

	&__meta {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 20px;
		line-height: 22px;
		letter-spacing: 0;
		color: var(--username-category-title);
		gap: 4px;
		word-break: break-word;
	}

	&__date {
		height: 40px;
		display: flex;
		align-items: flex-end;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 13px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: right;
		color: #9b9b9b;
	}

	&__author {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0px;
		color: var(--username-category-title);
		word-break: break-word;
	}
	&__second-row {
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 15px;
		line-height: 20px;
		letter-spacing: 0;
		color: var(--company-position-large);
		word-break: break-word;
	}

	&__follow {
		font-family: 'Inter', sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		color: var(--accent-color);
		text-decoration: unset !important;

		&-active {
			color: rgb(160, 160, 160);
		}
	}

	&__avatar {
		border-radius: 8px;
		img {
			object-fit: cover;
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
		border: 1px solid #e2e2e2;
		border-radius: 8px;
	}
	&__forums-title {
		padding: 12px;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		color: #4a4a4a;
		a {
			color: var(--link);
			text-decoration: none;
		}
	}

	@media screen and (max-width: 1904px) {
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__outer {
			flex-direction: column;
		}
	}
}
</style>
